/**
 * Signatures: capture on the office screen (e.g. the customer at the counter), and the
 * Manager's "Customer not present" with a reason (the technician can also record it on the iPad).
 * Each form the job needs has its own signature lines. Append-only; the latest counts.
 */
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SIGNATURE_FORM_LABELS, type Me, type SignatureForm, type SignatureRole } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import type { Sig, SignatureSet, SignaturesView } from '../../lib/form-types';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, Textarea } from '../../components/ui';
import { SignatureBox, type SignatureHandle } from '../../components/SignatureBox';
import { can } from '../../lib/perm';

export function SigImage({ label, sig }: { label: string; sig: Sig | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      {sig ? (
        <>
          <img src={`/api/files/${sig.fileId}`} alt={`${label} — ${sig.signerName}`} className="h-24 w-full rounded border bg-white object-contain" />
          <div className="text-xs text-slate-600">{sig.signerName} · {dateTime(sig.signedAt)}{sig.offline && ' · offline'}</div>
        </>
      ) : (
        <div className="flex h-24 items-center justify-center rounded border border-dashed text-sm text-slate-400">Not signed</div>
      )}
    </div>
  );
}

export function SignaturesTab({ woId, me, readOnly, onChange }: { woId: number; me: Me; readOnly: boolean; onChange: () => void }) {
  const q = useQuery({ queryKey: ['wo-signatures', woId], queryFn: () => api.get<SignaturesView>(`/api/work-orders/${woId}/signatures`) });
  const [capture, setCapture] = useState<{ form: SignatureForm; role: SignatureRole } | null>(null);
  const [notPresent, setNotPresent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (q.isLoading || !q.data) return <Loading />;
  const s = q.data;
  const isManager = can(me, 'jobs.review');
  const refresh = () => {
    void q.refetch();
    onChange();
  };

  const sets: SignatureSet[] = s.forms ?? [{ form: s.signatureForm, label: SIGNATURE_FORM_LABELS[s.signatureForm], certified: s.signatureForm === 'FIELD_REPORT' || s.signatureForm === 'LUBE', ...s.latest }];
  const labelOf = (form: SignatureForm) => sets.find((x) => x.form === form)?.label ?? SIGNATURE_FORM_LABELS[form];

  return (
    <div className="space-y-4">
      <ErrorText error={error} />
      <Card title="Customer not present">
        <p className="text-sm text-slate-600">Customer signatures aren't required: if the customer isn't there, the technician (on the iPad) or the Manager records why.</p>
        {s.customerNotPresentReason && <p className="mt-1 rounded bg-amber-50 p-2 text-sm">Customer not present: “{s.customerNotPresentReason}”</p>}
        {!readOnly && isManager && <Button className="mt-2" variant="secondary" onClick={() => setNotPresent(true)}>{s.customerNotPresentReason ? 'Change / clear “Customer not present”' : 'Customer not present'}</Button>}
      </Card>
      {sets.map((set) => (
        <Card key={set.form} title={`Signatures — ${set.label}`}>
          {set.certified && <p className="mb-3 text-xs leading-relaxed text-slate-600">{s.certification.text}</p>}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <SigImage label="Customer signature" sig={set.customer} />
              {!readOnly && <Button className="mt-2" variant="secondary" onClick={() => setCapture({ form: set.form, role: 'CUSTOMER' })}>Customer signs here</Button>}
            </div>
            <div>
              <SigImage label="Technician signature" sig={set.technician} />
              {!readOnly && isManager && <Button className="mt-2" variant="secondary" onClick={() => setCapture({ form: set.form, role: 'TECHNICIAN' })}>Technician signs here</Button>}
            </div>
          </div>
        </Card>
      ))}
      <Card title="All signatures (kept, newest counts)">
        {s.history.length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
        <ul className="space-y-1 text-sm">
          {s.history.map((h) => (
            <li key={h.id}>
              {labelOf(h.form)} · {h.role.toLowerCase()} · {h.signerName} · {dateTime(h.signedAt)}
              {h.statementVersion && <span className="text-slate-500"> · statement v{h.statementVersion}</span>}
              {h.offline && <span className="ml-1 rounded bg-slate-200 px-1 text-xs">offline</span>}
              <a className="ml-2 text-blue-700 underline" href={`/api/files/${h.fileId}`} target="_blank" rel="noreferrer">view</a>
            </li>
          ))}
        </ul>
      </Card>
      {capture && (
        <CaptureDialog
          role={capture.role}
          onClose={() => setCapture(null)}
          onSave={async (signerName, image) => {
            try {
              await api.post(`/api/work-orders/${woId}/signatures`, { form: capture.form, role: capture.role, signerName, image });
              setCapture(null);
              refresh();
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
      {notPresent && (
        <NotPresentDialog
          current={s.customerNotPresentReason}
          onClose={() => setNotPresent(false)}
          onSave={async (reason) => {
            try {
              await api.post(`/api/work-orders/${woId}/customer-not-present`, { reason });
              setNotPresent(false);
              refresh();
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
    </div>
  );
}

function CaptureDialog({ role, onClose, onSave }: { role: SignatureRole; onClose: () => void; onSave: (name: string, image: string) => void }) {
  const pad = useRef<SignatureHandle>(null);
  const [name, setName] = useState('');
  const [empty, setEmpty] = useState(true);
  return (
    <Modal
      title={role === 'CUSTOMER' ? 'Customer signature' : 'Technician signature'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={() => pad.current?.clear()}>Clear</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={empty || name.trim().length < 2} onClick={() => onSave(name.trim(), pad.current!.toDataURL())}>Save signature</Button>
        </>
      }
    >
      <SignatureBox ref={pad} height={180} onChange={setEmpty} />
      <Field label="Printed name" required><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
    </Modal>
  );
}

function NotPresentDialog({ current, onClose, onSave }: { current: string | null; onClose: () => void; onSave: (reason: string | null) => void }) {
  const [reason, setReason] = useState(current ?? '');
  return (
    <Modal
      title="Customer not present"
      onClose={onClose}
      footer={
        <>
          {current && <Button variant="secondary" onClick={() => onSave(null)}>Clear it</Button>}
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={reason.trim().length < 10} onClick={() => onSave(reason.trim())}>Save</Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">Closes without a customer signature. Logged, and shown on the weekly integrity report.</p>
      <Field label="Why couldn't the customer sign? (at least 10 characters)" required><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </Modal>
  );
}
